import { useValidateCheckIn } from 'hooks/appointments/useValidateCheckIn';
import { useCommonTranslation } from 'hooks/i18n/useCommonTranslation';
import {
  useMutateCheckInByCode,
  useMutateCheckInWithoutCode,
} from 'query/appointments';
import { useCallback } from 'react';
import { errorToast } from 'utils/toast';

import { usePatientCheckIn } from 'context/patient/modules/checkIn';
import { ErrorCode } from 'utils/errorCodes';
import { checkErrorCode } from 'utils/errors';
import { useSelfCheckInAppointments } from './useSelfCheckInAppointments';

export const useSelfCheckIn = ({
  onInvalidCode,
}: {
  onInvalidCode?: () => void;
} = {}) => {
  const { t } = useCommonTranslation();
  const { updateCheckIn } = usePatientCheckIn();
  const getEarlyLateCheckinErrorMessage = useValidateCheckIn();
  const { appts: apptsFound, isFetching: isQueryFetching } =
    useSelfCheckInAppointments();

  const mutationOptions = {
    onSuccess: () => {
      updateCheckIn({ success: true });
    },
    onError: (error: unknown) => {
      const earlyLateMessage = getEarlyLateCheckinErrorMessage(error);
      if (earlyLateMessage) {
        errorToast(earlyLateMessage);
      } else if (checkErrorCode(error, ErrorCode.INVALID_CODE_AP0011)) {
        if (onInvalidCode) {
          onInvalidCode();
        } else {
          errorToast(error);
        }
      } else {
        errorToast(error);
      }
    },
  };
  const { mutate: mutateByCode, isLoading: isCheckInByCodeLoading } =
    useMutateCheckInByCode(mutationOptions);
  const { mutate: mutateWithoutCode, isLoading: isCheckInWithoutCodeLoading } =
    useMutateCheckInWithoutCode(mutationOptions);
  const isLoading =
    isQueryFetching || isCheckInByCodeLoading || isCheckInWithoutCodeLoading;

  const resolveApptId = useCallback(() => {
    if (apptsFound.length === 0) {
      errorToast(t('patient-check-in-no-appointment'));
      return undefined;
    }
    return apptsFound.sort((a, b) => a.appt_time.localeCompare(b.appt_time))[0]
      .id;
  }, [apptsFound, t]);

  const checkInWithCode = useCallback(
    (code: number) => {
      const apptId = resolveApptId();
      if (apptId === undefined) {
        return;
      }
      mutateByCode({ apptId, code: code.toString() });
    },
    [resolveApptId, mutateByCode],
  );

  const checkInWithoutCode = useCallback(() => {
    const apptId = resolveApptId();
    if (apptId === undefined) {
      return;
    }
    mutateWithoutCode({ apptId });
  }, [resolveApptId, mutateWithoutCode]);

  return { isLoading, checkInWithCode, checkInWithoutCode };
};
